"use client";

import { useEffect, useMemo, useState } from "react";

import Block from "@/components/elements/Block";
import Container from "@/components/elements/Container";
import { useProduct } from "@/contexts/ProductContext";
import { getProductByID } from "@/lib/shopify";
import { recoverFromStaleServerAction } from "@/lib/errors/staleServerAction";
import { toGid } from "@/lib/shopify/gid";
import { findMatchingVariant } from "@/utils/utilities.mjs";
import rte, { rteHasContent } from "@/utils/rte";

import PriceDetails from "@/components/global/PriceDetails";
import Card from "./Card";
import Skeleton from "./Skeleton";

// Configure product that should be unlocked in the selector dropdown
const UNLOCKED_TYPES = ["pillow", "pillow case"];
const isUnlocked = (p) => UNLOCKED_TYPES.includes(p?.productType?.toLowerCase());

// buy_box_add_on_settings.product_description is plain text from the CMS, not rich text.
// Kept local rather than reusing utils/helpers.js's escapeHtmlAttribute, which is tuned
// for a different call site (stringifyMap) and could change out from under this modal.
const escapeHtml = (value) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const CrossSell = ({ block, position }) => {
  const { heading, content, settings } = block;
  const headingId = `cross-sells-${block.uid ?? (position !== undefined ? position : "default")}`;

  const {
    selectedVariant: mainSelectedVariant,
    setAdditionalProducts,
  } = useProduct();

  const [rawShopifyProducts, setRawShopifyProducts] = useState([]);
  const [selectedUids, setSelectedUids] = useState([]);
  const [manualVariants, setManualVariants] = useState({});
  const [loading, setLoading] = useState(true);

  // Normalize CMS product data to ensure consistent structure for Shopify fetching.
  const crossSellItems = useMemo(() => {
    const items = block.products || block.cross_sells || [];
    
    return items
      .map((item) => {
        // Handle both direct product references and nested reference structures.
        const productData = item?.product?.[0] || item;

        return {
          uid: item._uid ?? productData?.uid ?? productData?.shopify_product_id,
          shopifyProductId: productData?.shopify_product_id,
          productTitle: productData?.product_name || productData?.title,
          modalDescription: productData?.buy_box_add_on_settings?.product_description || null,
          cmsImage: productData?.buy_box_add_on_settings?.product_image?.url
            ? productData.buy_box_add_on_settings.product_image
            : null,
          modal: item.modal || productData?.modal || null,
        };
      })
      .filter((item) => item.shopifyProductId);
  }, [block.products, block.cross_sells]);

  // Initial fetch: Retrieve full Shopify product data for all cross-sell items.
  useEffect(() => {
    let isMounted = true;
    async function init() {
      if (crossSellItems.length === 0) {
        if (isMounted) setLoading(false);
        return;
      }

      const results = await Promise.all(
        crossSellItems.map(async (item) => {
          try {
            const product = await getProductByID(toGid("Product", item.shopifyProductId));
            return product ? { ...item, product } : null;
          } catch (error) {
            console.error("Failed to load cross-sell product", item.shopifyProductId, error);
            recoverFromStaleServerAction(error, {
              stage: "cross_sell",
              productId: item.shopifyProductId,
            });
            return null;
          }
        }),
      );

      if (isMounted) {
        setRawShopifyProducts(results.filter(Boolean));
        setLoading(false);
      }
    }
    init();
    return () => {
      isMounted = false;
    };
  // Re-fetch on variant change to get fresh availableForSale stock data.
  }, [crossSellItems, mainSelectedVariant?.id]);

  // Synchronously derive matching variants based on the main product's selection.
  const crossSellProducts = useMemo(() => {
    return rawShopifyProducts
      .map((item) => {
        const unlocked = isUnlocked(item.product);
        const variant =
          (unlocked &&
            item.product.variants.find((v) => v.id === manualVariants[item.uid])) ||
          findMatchingVariant(mainSelectedVariant, item.product);

        if (!variant) return null;

        const shopifyImage = variant.image || item.product.featuredImage || null;

        // Modal description always comes from the CMS product's add-on settings.
        const finalDescription = item.modalDescription
          ? `<p>${escapeHtml(item.modalDescription)}</p>`
          : null;

        return {
          ...item,
          image: item.cmsImage || shopifyImage,
          description: finalDescription,
          modalLabel: item.modal?.label || "Learn More",
          isUnlocked: unlocked,
          shopifyData: { product: item.product, variant },
        };
      })
      .filter(Boolean);
  }, [rawShopifyProducts, mainSelectedVariant, manualVariants]);

  // Derive active selection and prune any items that are no longer available for sale.
  const selectedCrossSells = useMemo(() => {
    return selectedUids
      .map((uid) => crossSellProducts.find((p) => p.uid === uid))
      .filter((p) => p?.shopifyData?.variant?.availableForSale === true);
  }, [selectedUids, crossSellProducts]);

  // Synchronize cross-sell selections with the global ProductContext.
  // We use stable CMS UIDs instead of variant IDs to maintain selections across variant changes.
  useEffect(() => {
    const currentScopeUids = crossSellProducts.map((p) => p.uid).filter(Boolean);

    setAdditionalProducts((prev) => {
      // Keep products not managed by this block (e.g., global addons).
      // We identify local cross-sells using the persistent `_sourceUid`.
      const unrelatedItems = prev.filter((item) => !currentScopeUids.includes(item._sourceUid));
      
      // Add currently selected cross-sells, augmented with metadata for the cart button.
      const selectedItems = selectedCrossSells.map((s) => ({
        ...s.shopifyData.variant,
        title: s.productTitle,
        variantTitle: s.shopifyData.variant.title,
        _sourceUid: s.uid,
      })).filter(Boolean);

      const next = [...unrelatedItems, ...selectedItems];

      // Return prev state if IDs match to avoid unnecessary re-renders
      return prev.length === next.length && prev.every((v, i) => v.id === next[i].id) ? prev : next;
    });
  }, [selectedCrossSells, crossSellProducts, setAdditionalProducts]);

  function toggleCrossSell(crossSell) {
    const isSelected = selectedUids.includes(crossSell.uid);

    if (isSelected) {
      setSelectedUids((prev) => prev.filter((uid) => uid !== crossSell.uid));
    } else {
      setSelectedUids((prev) => [...prev, crossSell.uid]);
    }
  }

  return (
    <div
      id={`${block.uid}-${position}`}
      data-name="cross-sell"
      data-position={position}
      className="bg-warm-white"
    >
      <Container className="p-4 grid grid-flow-row gap-y-4">
        <div className="flex flex-col gap-1">
          {heading && (
            <div
              id={headingId}
              className={`font-worksans text-base font-semibold leading-6 ${settings?.text_color?.color_selector ?? "text-primary"}`}
            >
              {heading}
            </div>
          )}
          {rteHasContent(content) && (
            <div
              className={`font-worksans text-base leading-6 rte-content [&_a]:text-sea [&_a]:underline [&_a]:font-medium ${settings?.text_color?.color_selector ?? "text-primary"}`}
              dangerouslySetInnerHTML={{ __html: rte(content) }}
            />
          )}
          <PriceDetails className="w-fit" />
        </div>

        {loading
          ? crossSellItems.map((item, i) => (
              <Skeleton key={item.uid || i} />
            ))
          : crossSellProducts.map((crossSell) => {
              const isChecked = selectedCrossSells.some(
                (s) => s.uid === crossSell.uid,
              );
              const isSoldOut =
                !crossSell.shopifyData.variant.availableForSale;

              return (
                <Card
                  key={crossSell.uid}
                  productTitle={crossSell.productTitle}
                  image={crossSell.image}
                  description={crossSell.description}
                  modalLabel={crossSell.modalLabel}
                  selectedVariant={crossSell.shopifyData.variant}
                  isChecked={isChecked}
                  isSoldOut={isSoldOut}
                  toggleCrossSell={() => toggleCrossSell(crossSell)}
                  isUnlocked={crossSell.isUnlocked}
                  variants={crossSell.product.variants}
                  onVariantChange={(id) =>
                    setManualVariants((v) => ({ ...v, [crossSell.uid]: id }))
                  }
                />
              );
            })}
      </Container>
    </div>
  );
};

export default CrossSell;
